import { useEffect, useRef } from 'react';

/**
 * Prati element IntersectionObserverom i poziva callback kad se vidljivost promijeni.
 *
 * @param {Function} onIntersect Funkcija koja se poziva s (entry) kad element uđe u viewport,
 *                               a uz `trackExit: true` i kad izađe (provjeri entry.isIntersecting).
 * @param {Object} [options] Postavke observera.
 * @param {boolean} [options.once=true] Ako je true, callback se poziva samo pri prvom ulasku i observer se
 *                                      odmah gasi. Ako je false, poziva se pri svakom ulasku.
 *                                      Default vrijednost: true.
 * @param {boolean} [options.trackExit=false] Ako je true, callback se poziva i kad element izađe iz viewporta.
 *                                            Ima efekta samo uz `once: false`.
 *                                            Default vrijednost: false.
 * @param {number|number[]} [options.threshold=0.25] Koliki dio elementa (0 do 1) mora biti vidljiv da se
 *                                                   smatra da je ušao. Niz stavi u konstantu izvan komponente.
 *                                                   Default vrijednost: 0.25.
 * @param {boolean} [options.enabled=true] Ako je false, ništa se ne promatra (npr. za frame servis).
 *                                         Default vrijednost: true.
 * @returns {React.RefObject} Ref koji se stavlja na promatrani element.
 */
export default function useIntersectionRef(onIntersect, {
    once = true,
    trackExit = false,
    threshold = 0.25,
    enabled = true,
} = {}) {
    const ref = useRef(null);
    // Callback držimo u refu da se observer ne stvara iznova kad se promijeni funkcija.
    const callbackRef = useRef(onIntersect);
    callbackRef.current = onIntersect;

    useEffect(() => {
        const element = ref.current;
        if (!enabled || !element || typeof IntersectionObserver === 'undefined') return undefined;

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                callbackRef.current(entry);
                if (once) observer.disconnect();
            } else if (trackExit && !once) {
                callbackRef.current(entry);
            }
        }, { threshold });
        observer.observe(element);

        return () => observer.disconnect();
    }, [enabled, once, trackExit, threshold]);

    return ref;
}
